"use client"

import type { Task } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Calendar, CheckCircle2, Circle, Clock, AlertTriangle, Filter, Eye, EyeOff, Search } from "lucide-react"
import { formatDate } from "@/lib/time-utils"
import { cn } from "@/lib/utils"
import { getStatusColors } from '@/lib/status-colors';
import { useTimer } from "@/lib/timer-context"
import { useState } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"

interface TaskSelectorProps {
  tasks: Task[]
  selectedTask: Task | null
  onSelectTask: (task: Task) => void
}

export function TaskSelector({ tasks, selectedTask, onSelectTask }: TaskSelectorProps) {
  const { isRunning, selectedTask: activeTask } = useTimer()
  const [showWarning, setShowWarning] = useState(false)

  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [filterDueDate, setFilterDueDate] = useState<string>("")
  const [showFilters, setShowFilters] = useState(false)
  const [showCompleted, setShowCompleted] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  const getStatusIcon = (status: Task["status"]) => {
    switch (status) {
      case "Backlog":
        return <Circle className="h-5 w-5 text-slate-500 flex-shrink-0" />
      case "Not Started":
        return <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      case "In Progress":
        return <Clock className="h-5 w-5 text-blue-500 flex-shrink-0" />
      case "Quality Check":
        return <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
      case "Done":
        return <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
      default:
        return <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
    }
  }

  const handleTaskSelect = (task: Task) => {
    if (isRunning && activeTask && activeTask.id !== task.id) {
      setShowWarning(true)
      setTimeout(() => setShowWarning(false), 4000)
      return
    }
    onSelectTask(task)
  }

  const filteredTasks = tasks.filter((task) => {
    if (!showCompleted && task.status === "Done") return false
    if (filterStatus !== "all" && task.status !== filterStatus) return false
    if (filterDueDate && task.due) {
      const taskDueDate = task.due.toISOString().split("T")[0]
      if (taskDueDate !== filterDueDate) return false
    }
    if (searchTerm.trim()) {
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()))
      if (!matchesSearch) return false
    }
    return true
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Select a Task</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Choose a task to start tracking time</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showCompleted ? "default" : "outline"}
            size="sm"
            onClick={() => setShowCompleted(!showCompleted)}
            className={`gap-2 ${showCompleted ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
          >
            {showCompleted ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showCompleted ? "Hide" : "Show"} Done
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search tasks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {showWarning && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900 dark:text-red-300">
              You cannot change tasks while a timer is running. Please stop the current timer first.
            </p>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Filter by Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Backlog">Backlog</SelectItem>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Quality Check">Quality Check</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filter by Due Date</Label>
              <Input type="date" value={filterDueDate} onChange={(e) => setFilterDueDate(e.target.value)} />
            </div>
          </div>
          {(filterStatus !== "all" || filterDueDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterStatus("all")
                setFilterDueDate("")
              }}
              className="mt-4"
            >
              Clear Filters
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filteredTasks.map((task) => {
          const isDisabled = isRunning && activeTask && activeTask.id !== task.id
          const isSelected = selectedTask?.id === task.id

          return (
            <div
              key={task.id}
              className={cn(
                "bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border dark:border-gray-700 p-4 cursor-pointer transition-all hover:shadow-md",
                isSelected && "ring-2 ring-primary shadow-md",
                isDisabled && "opacity-50 cursor-not-allowed hover:shadow-sm",
              )}
              style={{ borderLeftColor: getStatusColors(task.status).borderColorValue }}
              onClick={() => handleTaskSelect(task)}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight line-clamp-2 flex-1">{task.title}</h3>
                <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                  {getStatusIcon(task.status)}
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span 
                  className={`text-xs px-2 py-1 rounded-md font-medium ${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}
                >
                  {task.status}
                </span>
                {task.due && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Calendar className="h-3 w-3" />
                    {formatDate(task.due)}
                  </div>
                )}
              </div>

              {/* Time tracking info */}
              {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mb-2">
                  <Clock className="h-3 w-3" />
                  {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                    <span>{task.realisedHours.toFixed(1)}h / {task.estimatedHours}h</span>
                  ) : task.realisedHours !== undefined ? (
                    <span>{task.realisedHours.toFixed(1)}h tracked</span>
                  ) : (
                    <span>{task.estimatedHours}h estimated</span>
                  )}
                </div>
              )}

              {/* Tags */}
              {task.tags && task.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {task.tags.map((tag, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{task.description}</p>
            </div>
          )
        })}
      </div>

      {filteredTasks.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {!showCompleted && tasks.some((t) => t.status === "Done")
            ? "No active tasks. Click 'Show Done' to view done tasks."
            : "No tasks match your filters"}
        </div>
      )}
    </div>
  )
}
